// Slack compositions used by the Groomba guide page.
// Each export renders a complete modal, message, or App Home surface that
// recreates one of the screenshots in the original guide. All compositions
// expect to live inside a `.slack-root` (the SkFigure wrapper provides this).

import React from 'react';
import {
  SkModal, SkField, SkRow, SkInput, SkSelect, SkCheck, SkUserField,
  SkChipField, SkBtn, SkHint, SkDivider,
  SkAppHome, SkAppHomeSection, SkAppHomeActions, SkAppHomePoll,
  SkMessage, SkIssueTitle, SkMetaRow, SkVoteRow, SkAwaiting, SkClosing,
  SkLiveVoters, SkOverflowMenu, SkLink, SkMention, SkCode,
  IcoTask, IcoPriority,
} from '../Slack';

// favicon used as the Groomba app icon in modals
const FAV = '/packs/media/images/favicon-aa9b1ddfa9e4f8a3680f25b3d8b2cd3a.png';

// We accept iconSrc via props (provided by guide.js mount) so we don't need to
// know the asset fingerprint here. Each composition takes an optional
// iconSrc; if absent, the modal/message simply renders without the icon.

/* ── App Home tab ─────────────────────────────────────────── */
export function SlackAppHome({ iconSrc }) {
  const pollMeta = (expires) => ([
    { label: 'Expires', value: expires },
    { icon: <IcoTask/>, value: 'Task' },
    { icon: <IcoPriority/>, label: 'Priority', value: 'Medium' },
    { value: <SkLink soft>(link to poll in Slack)</SkLink> },
  ]);
  return (
    <SkAppHome appName="Groomba" iconSrc={iconSrc}>
      <SkAppHomeSection title="Quick actions">
        <SkAppHomeActions actions={[
          { label: 'Create new team',   variant: 'primary' },
          { label: 'Groom Jira issues', variant: 'primary' },
          { label: 'View all teams',    variant: 'outline' },
          { label: 'Help',              variant: 'outline' },
          { label: 'Guide',             variant: 'outline' },
          { label: 'Contact',           variant: 'pink' },
        ]}/>
      </SkAppHomeSection>

      <SkAppHomeSection title="Your Stats">
        <div className="sk-apphome-stat">
          <span className="stat-ico" aria-hidden="true">★</span>
          <span>No current vote streak…</span>
        </div>
      </SkAppHomeSection>

      <SkAppHomeSection title="Open polls">
        <div className="sk-apphome-polls">
          <SkAppHomePoll id="STTP-11" title="Throttle Jira webhook retries"  meta={pollMeta('Today at 9:34 PM')} note="You haven't voted yet." />
          <SkAppHomePoll id="STTP-13" title="Backfill missing user timezones" meta={pollMeta('Today at 9:35 PM')} note="You haven't voted yet." />
        </div>
      </SkAppHomeSection>
    </SkAppHome>
  );
}

/* ── Create team modal ────────────────────────────────────── */
export function SlackCreateTeamModal({ iconSrc }) {
  return (
    <SkModal
      title="Let's refine a backlog!"
      iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Begin</SkBtn></>}
    >
      <SkHint style={{margin:'4px 0 14px'}}>
        A <strong>Groomba team</strong> collaborates in Slack to refine a Jira backlog.{' '}
        <SkLink>Read the guide/FAQ at app.groomba.ai</SkLink>
      </SkHint>
      <SkField label="What team is refining this backlog?">
        <SkInput value="Platform Core" counter={46} />
      </SkField>
      <SkField>
        <SkCheck checked title="Create new channel" desc="If checked, creates a new private channel for your team." />
      </SkField>
      <SkField label="Team participants" help="People who can vote in estimation polls.">
        <SkUserField users={[{name:'Alex Chen (you)', initials:'AC'}]} />
      </SkField>
      <SkRow label="Which Jira Cloud contains your space?">
        <SkSelect compact value="yeeter.atlassian.net" options={['yeeter.atlassian.net']}/>
      </SkRow>
      <SkRow label="What type of backlog?" optional>
        <SkSelect compact value="Scrum board" options={['None','Scrum board','Kanban board']}/>
      </SkRow>
      <SkField label="Choose estimate field">
        <SkSelect value="Story point estimate" options={['Story point estimate','Story Points','Dev Story Points']}/>
      </SkField>
    </SkModal>
  );
}

/* ── Groom issues modal — ad-hoc /groomba ISSUE-1 ISSUE-2 ── */
export function SlackGroomIssuesModal({ iconSrc }) {
  return (
    <SkModal
      title="Groom Jira issues"
      iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Start polls</SkBtn></>}
    >
      <SkHint style={{margin:'4px 0 14px'}}>
        Poll any Jira issues you can read. Backlog filters do <em>not</em> apply, so you can poll things outside your team's normal scope.
      </SkHint>
      <SkField label="Issues to estimate">
        <SkInput value="ISSUE-1, ISSUE-2" />
      </SkField>
      <SkField label="Channel">
        <SkSelect value="#sprint-estimation" options={['#sprint-estimation','#planning','#engineering']}/>
      </SkField>
      <SkField>
        <SkCheck title="Notify the channel" desc="Sends @here when polls open." />
      </SkField>
    </SkModal>
  );
}

/* ── Voting message ──────────────────────────────────────── */
export function SlackVotingMessage({ iconSrc }) {
  const meta = [
    { icon: <IcoTask/>,     label:'Type',     value:'Task' },
    { icon: <IcoPriority/>, label:'Priority', value:'Medium' },
  ];
  return (
    <SkMessage iconSrc={iconSrc} time="9:32 PM">
      <SkIssueTitle id="STTP-13">Backfill missing user timezones</SkIssueTitle>
      <SkMetaRow items={meta}/>
      <div style={{fontSize:13, color:'var(--sk-muted)', margin:'6px 0 4px', fontWeight:600}}>Cast your vote:</div>
      <SkVoteRow options={['1','2','3','5','8','13','?','Defer','Flag']} chosen="5"/>
      <SkLiveVoters static voters={[
        { name:'Alex Chen', value:'5' },
      ]}/>
      <SkAwaiting mentions={['Dana Lee','Priya Rao']}/>
      <SkClosing time="Today at 10:00 PM" issueId="STTP-13" action="estimated at 4 points"/>
    </SkMessage>
  );
}

/* ── Post-voting menu (View votes / Re-estimate / Undo) ──── */
export function SlackPostVotingOptions({ iconSrc }) {
  return (
    <>
      <SkMessage
        iconSrc={iconSrc}
        time="9:34 PM"
        actions={<SkOverflowMenu align="left" static items={[
          { label: 'View votes' },
          { label: 'Re-estimate' },
          { label: 'Undo' },
        ]}/>}
      >
        <div style={{fontSize:15, lineHeight:1.5}}>
          Team <strong>Platform Core</strong> estimated{' '}
          <SkLink>[STTP-1] Cache backlog filter results</SkLink> to be{' '}
          <strong>0.5 story points</strong>.
        </div>
      </SkMessage>
      <SkMessage grouped>
        <div style={{fontSize:14}}>
          <SkMention>Alex Chen</SkMention> voted <strong>0.5</strong> story points.
        </div>
      </SkMessage>
    </>
  );
}

/* ── Auto-polling settings (full modal) ─────────────────── */
export function SlackAutoPollSettings({ iconSrc }) {
  return (
    <SkModal title="Auto-polling settings" iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Save</SkBtn></>}>
      <SkRow label="Maximum issues open from auto-polling backlog">
        <SkSelect compact value="Intelligent" options={['0 (off)','1','2','3','5','8','13','21','99','Intelligent']}/>
      </SkRow>
      <SkRow label="How long before auto-polling deferred issues again?">
        <SkSelect compact value="1 day" options={['6 hours','12 hours','1 day','3 days','1 week','2 weeks','1 month','Never']}/>
      </SkRow>
      <SkHint style={{margin:'-2px 0 14px'}}>
        Optionally limit how many polls auto-polling can start in a given time period. Set to Unlimited to remove the cap. Manual polls are never affected.
      </SkHint>
      <SkRow label="Max auto-polls per time window">
        <SkSelect compact value="10" options={['Unlimited','1','3','5','10','20','50','100','200','500']}/>
      </SkRow>
      <SkRow label="Time window">
        <SkSelect compact value="1 day" options={['1 day','3 days','1 week','2 weeks','1 month']}/>
      </SkRow>
      <SkDivider/>
      <SkHint style={{margin:'-2px 0 14px'}}>
        Control <strong>when</strong> auto-polling runs. Reminders are unaffected.
      </SkHint>
      <SkRow label="Timezone">
        <SkSelect compact value="(GMT-05:00) Eastern" options={[
          '(GMT-08:00) Pacific','(GMT-07:00) Mountain','(GMT-06:00) Central',
          '(GMT-05:00) Eastern','(GMT+00:00) UTC','(GMT+01:00) Central European',
        ]}/>
      </SkRow>
      <SkField label="Days of the week">
        <SkChipField chips={['Mon','Tue','Wed','Thu','Fri']} focused />
      </SkField>
      <SkField>
        <SkCheck title="Limit to certain times"
          desc="If left unchecked, auto-polling can occur at any time of day on your configured days of the week."/>
      </SkField>
    </SkModal>
  );
}

/* ── Backlog settings ──────────────────────────────────── */
export function SlackBacklogSettings({ iconSrc }) {
  return (
    <SkModal title="Backlog settings" iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Save</SkBtn></>}>
      <SkRow label="What type of backlog?">
        <SkSelect compact value="Jira board" options={['None','Jira board','Project','Saved filter']}/>
      </SkRow>
      <SkRow label="Select a board">
        <SkSelect compact value="Platform Sprint Board" options={['Platform Sprint Board','Growth Kanban','Infra Backlog']}/>
      </SkRow>
      <SkHint style={{margin:'-6px 0 14px'}}>
        Boards preserve rank ordering, so your most important issues get estimated first.
      </SkHint>
      <SkField label="Choose estimate field">
        <SkSelect value="Story point estimate" options={['Story point estimate','Story Points','Dev Story Points']}/>
      </SkField>
      <SkRow label="Skip automatic polling for selected issue types">
        <SkSelect compact value="Epic" options={['Epic','Bug','Sub-task']}/>
      </SkRow>
    </SkModal>
  );
}

/* ── Reminder settings ────────────────────────────────── */
export function SlackReminderSettings({ iconSrc }) {
  return (
    <SkModal title="Reminder settings" iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Save</SkBtn></>}>
      <SkHint style={{margin:'4px 0 14px'}}>
        Configure or disable when participants are sent reminders about polls. Reminders are{' '}
        <em>batched</em>: a participant gets one message covering every poll they still owe an
        estimate on, never one message per poll.
      </SkHint>
      <SkDivider/>
      <SkRow label="Remind participants when new poll has started?">
        <SkSelect compact value="Off" options={['10 min after','25 min after','55 min after','Off']}/>
      </SkRow>
      <SkRow label="Remind participants when poll is about to expire?">
        <SkSelect compact value="Off" options={['30 min before','1 hour before','Off']}/>
      </SkRow>
      <SkDivider/>
      <SkHint style={{margin:'-2px 0 14px'}}>
        Separately, remind everyone with an open poll on a recurring schedule (like a calendar
        reminder), regardless of when polls opened or expire. The two settings above fire at most
        once each per poll; this one repeats until the poll closes or they vote.
      </SkHint>
      <SkRow label={<strong>Recurring reminder</strong>}>
        <SkSelect compact focused value="Weekly" options={['Off','Daily (weekdays)','Weekly','Every 2 weeks','Monthly']}/>
      </SkRow>
      <SkField label="Which days?">
        <SkChipField chips={['Monday']} />
      </SkField>
      <SkRow label="Time of day">
        <SkSelect compact value="10:00am" options={['4:00am','7:00am','9:00am','10:00am','1:00pm','4:00pm','7:00pm']}/>
      </SkRow>
      <SkHint style={{margin:'-2px 0 14px'}}>
        <strong>✍️ Description:</strong> Every week on Monday at 10:00am (GMT-05:00)<br/>
        <strong>🗓 Next reminder:</strong> Monday, August 17, 2026 at 10:00am
      </SkHint>
      <SkDivider/>
      <SkHint style={{margin:'-2px 0 4px'}}>
        You can submit feedback about reminders with <SkCode>/groomba feedback</SkCode>.
      </SkHint>
    </SkModal>
  );
}

/* ── Averaging settings ───────────────────────────────── */
export function SlackAveragingSettings({ iconSrc }) {
  return (
    <SkModal title="Averaging settings" iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Save</SkBtn></>}>
      <SkHint style={{margin:'4px 0 14px'}}>
        Configure how Groomba averages your team's votes. If you have questions or requests, type{' '}
        <SkCode>/groomba feedback</SkCode> and we will typically respond <strong>within 24 hours</strong>.
      </SkHint>
      <SkDivider/>
      <SkRow label="Snap result to scale?">
        <SkSelect compact focused value="No" options={['No','Yes']}/>
      </SkRow>
      <SkRow label="Rounding precision">
        <SkSelect compact value="0.5 points" options={['Whole points','0.5 points','Tenths']}/>
      </SkRow>
      <SkRow label="Ceiling/floor/round function">
        <SkSelect compact value="Round" options={['Ceiling','Floor','Round']}/>
      </SkRow>
      <SkRow label="Rounding cutoff">
        <SkSelect compact value="0.25" options={['0.1','0.2','0.25','0.3','0.4','0.5']}/>
      </SkRow>
      <SkRow label="Averaging method">
        <SkSelect compact value="Mean" options={['Mean','Median','Geometric mean','Harmonic mean']}/>
      </SkRow>
      <div style={{display:'flex', alignItems:'center', gap:8, fontSize:15, fontWeight:700, margin:'14px 0 8px'}}>
        <span>Test your configuration</span>
      </div>
      <SkInput value="5.0, 5.0"/>
      <div className="sk-help" style={{margin:'6px 0 12px'}}>
        Example votes to test your configuration on e.g. '1, 1, 3, 2'.
      </div>
      <p style={{margin:'4px 0 10px', fontSize:14, lineHeight:1.5}}>
        Example votes would average to <strong>5.0 story points</strong>, which is what would be set in Jira.
      </p>
    </SkModal>
  );
}

/* ── Poll display settings + live preview ─────────────── */
export function SlackPollDisplaySettings({ iconSrc }) {
  const meta = [
    { icon: <IcoTask/>, label:'Type', value:'Story' },
    { icon: <IcoPriority/>, label:'Priority', value:'Medium' },
  ];
  return (
    <SkModal title="Poll Display Settings" iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Save</SkBtn></>}>
      <SkHint style={{margin:'4px 0 14px'}}>
        Configure how polls appear in your Slack channel. Changes apply to all new polls.
      </SkHint>
      <SkDivider/>
      <SkField label="Display options (6)">
        <SkChipField focused chips={[
          'Issue descriptions',
          'Running result',
          'Vote results',
          'Awaiting voters',
          '"Cast your vote" header',
          'Poll requester',
        ]}/>
      </SkField>
      <SkRow label="Show vote buttons in a single row?">
        <SkSelect compact value="No" options={['No','Yes']}/>
      </SkRow>
      <SkDivider/>
      <div className="sk-issue-title"><a href="#">[DEMO-1] Example issue</a></div>
      <div style={{fontStyle:'italic', color:'var(--sk-text-2)', margin:'-4px 0 8px', fontSize:14}}>
        Issue description would appear here…
      </div>
      <SkMetaRow items={meta}/>
      <div style={{fontWeight:700, margin:'4px 0 8px', fontSize:14}}>Cast your vote for DEMO-1:</div>
      <SkVoteRow layout="grid" options={['1/2','5','Defer','Flag','Resolve']}/>
      <div className="sk-vote-line" style={{marginTop:6}}>
        <SkMention>Dana Lee</SkMention> voted <strong>3</strong> story points.
      </div>
      <SkAwaiting mentions={['Alex Chen']}/>
    </SkModal>
  );
}

/* ── Team ownership ───────────────────────────────────── */
export function SlackOwnershipSettings({ iconSrc }) {
  return (
    <SkModal title="Ownership settings" iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Transfer</SkBtn></>}>
      <SkRow label="Primary owner"><SkSelect compact value="Alex Chen" options={['Alex Chen','Dana Lee','Priya Rao']}/></SkRow>
      <SkHint style={{margin:'-6px 0 14px'}}>
        The primary owner's Jira credentials are used for all API calls on this team.
      </SkHint>
      <SkField label="Secondary owners" help="Can modify team settings but cannot transfer primary ownership.">
        <SkUserField users={[
          { name:'Dana Lee',  initials:'DL' },
          { name:'Priya Rao', initials:'PR' },
        ]}/>
      </SkField>
    </SkModal>
  );
}

/* ── Workspace settings ───────────────────────────────── */
export function SlackWorkspaceSettings({ iconSrc }) {
  return (
    <SkModal title="Workspace settings" iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Save</SkBtn></>}>
      <SkRow label="Primary workspace admin">
        <SkSelect compact value="Alex Chen" options={['Alex Chen','Dana Lee']}/>
      </SkRow>
      <SkField label="Secondary admins" help="Can see and manage all teams from the portal.">
        <SkUserField users={[{ name:'Dana Lee', initials:'DL' }]}/>
      </SkField>
      <SkDivider/>
      <div style={{fontSize:12, textTransform:'uppercase', letterSpacing:'.08em', color:'var(--sk-muted)', fontWeight:700, margin:'4px 0 10px'}}>
        Teams in this workspace
      </div>
      <div style={{display:'flex', flexDirection:'column', gap:6}}>
        {['Platform Core','Growth','Infra'].map((t,i)=>(
          <div key={t} style={{display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 10px', background:'var(--sk-surface-2)', borderRadius:4}}>
            <span><strong>{t}</strong> · #{t.toLowerCase().replace(/\s/g,'-')}</span>
            <span style={{color:'var(--sk-muted)'}}>{[6,4,3][i]} seats</span>
          </div>
        ))}
      </div>
    </SkModal>
  );
}

/* ── Portal open message ─────────────────────────────── */
export function SlackPortalMessage({ iconSrc }) {
  return (
    <SkMessage iconSrc={iconSrc} time="2:14 PM">
      <p style={{margin:0, fontSize:14}}>
        Open the Groomba web portal to see all your teams in one place.
      </p>
      <div style={{marginTop:10}}>
        <SkBtn primary>Open portal</SkBtn>
      </div>
      <SkHint style={{margin:'8px 0 0'}}>Link expires in 10 minutes. Only visible to you.</SkHint>
    </SkMessage>
  );
}

/* ── Manage subscription message ─────────────────────── */
export function SlackManageSubscription({ iconSrc }) {
  return (
    <SkMessage iconSrc={iconSrc} time="11:02 AM">
      <p style={{margin:'0 0 8px', fontSize:14}}>
        Your workspace is on the <strong>Standard</strong> plan — billed monthly based on active seats.
      </p>
      <div style={{padding:'10px 12px', background:'var(--sk-surface-2)', borderRadius:6, fontSize:13, margin:'8px 0'}}>
        <div style={{display:'flex', justifyContent:'space-between'}}>
          <span>Current period</span><strong>8 billable seats × $4 = $32.00</strong>
        </div>
        <div style={{display:'flex', justifyContent:'space-between', color:'var(--sk-muted)', marginTop:4}}>
          <span>Next invoice</span><span>May 1, 2026</span>
        </div>
      </div>
      <div style={{display:'flex', gap:8, marginTop:4}}>
        <SkBtn primary>Manage subscription</SkBtn>
        <SkBtn>View invoices</SkBtn>
      </div>
    </SkMessage>
  );
}

/* ── Workspace listing modal (/groomba workspace) ────── */
export function SlackWorkspaceListing({ iconSrc }) {
  const teams = [
    { name: 'Platform Core', channel: '#platform-core', owner: 'Alex Chen' },
    { name: 'Growth',        channel: '#growth-est',    owner: 'Dana Lee' },
  ];
  return (
    <SkModal title="Groomba workspace" iconSrc={iconSrc}
      actions={<SkBtn>Close</SkBtn>}>
      <div style={{fontSize: 15, lineHeight: 1.55}}>
        <p style={{margin: '0 0 14px'}}>
          Your role: <strong>Owner</strong>. Primary admin: <SkMention awaiting>Alex Chen</SkMention>.
        </p>
        <p style={{margin: '0 0 8px'}}>All Groomba teams:</p>
        <ul style={{margin: '0 0 14px', padding: 0, listStyle: 'none'}}>
          {teams.map(t => (
            <li key={t.name} style={{margin: '0 0 6px'}}>
              {'- '}<strong>{t.name}</strong>{' '}
              <SkLink>{t.channel}</SkLink>{' '}
              [owned by <SkMention awaiting>{t.owner}</SkMention>]
            </li>
          ))}
        </ul>
        <SkHint style={{fontStyle:'italic'}}>
          Disconnected or archived teams can be viewed and deleted at <SkLink>app.groomba.ai/portal</SkLink>.
        </SkHint>
      </div>
      <SkDivider/>
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16}}>
        <div style={{fontSize: 15, maxWidth: 360}}>
          Manage workspace admins and transfer primary ownership
        </div>
        <SkBtn>Workspace settings</SkBtn>
      </div>
    </SkModal>
  );
}

/* ── Feedback modal ───────────────────────────────────── */
export function SlackFeedbackModal({ iconSrc }) {
  return (
    <SkModal title="Send us feedback" iconSrc={iconSrc}
      actions={<><SkBtn>Cancel</SkBtn><SkBtn primary>Send</SkBtn></>}>
      <SkHint style={{margin:'4px 0 14px'}}>
        We read every message. You'll usually hear back within a day.
      </SkHint>
      <SkRow label="Type">
        <SkSelect compact value="Feature request" options={['Bug report','Feature request','Question','Other']}/>
      </SkRow>
      <SkField label="What's on your mind?">
        <textarea className="sk-input" rows="4" defaultValue="Would love a way to skip weekends in auto-polling…" style={{resize:'vertical', fontFamily:'inherit'}}/>
      </SkField>
      <SkField>
        <SkCheck checked title="Include my Slack handle" desc="So we can follow up if we need more detail."/>
      </SkField>
    </SkModal>
  );
}
